import {
  RequestTreasuryBillCommand,
  PaginatedListOfTreasuryBillListingDto,
  TreasuryBillSummaryDto,
  PaginatedListOfTreasuryBillSummaryDto,
  TreasuryBillDto,
  TreasuryBillListingNestedDto,
  TreasuryBillTransactionDto,
  TransactionType,
  OrderStatus,
  IssuerDto,
  CreateTreasuryBillSellOrderCommand,
} from '@generated/api';
import {
  RequestTreasuryBill,
  TreasuryBillAssetSummary,
  TreasuryBillListing,
  TreasuryBillDetail,
  TreasuryBillListingNested,
  TreasuryBillTransaction,
  IssuerInfo,
  SellTreasuryBill,
  TreasuryBillLifecycle,
  TreasuryBillTransactionType,
} from '@typeDefs/domain/assets';
import { PaginatedResponse } from '@typeDefs/domain/shared';

export const treasuryBillLifecycleFromStatus = (
  status: OrderStatus | number | undefined,
  faceValue: number,
): TreasuryBillLifecycle | undefined => {
  switch (status) {
    case OrderStatus.Value0:
      return 'pending';
    case OrderStatus.Value1:
      return 'active';
    case OrderStatus.Value2:
      return 'rejected';
    case OrderStatus.Value3:
      return faceValue === 0 ? 'sold' : 'matured';
    default:
      return undefined;
  }
};

export const treasuryBillMapper = {
  summaryFromApi: (apiData: TreasuryBillSummaryDto): TreasuryBillAssetSummary => ({
    id: apiData.id || '',
    title: apiData.title || '',
    isin: apiData.isin || '',
    currentEstimatedValue: apiData.currentEstimatedValue || 0,
    maturityDate: apiData.maturityDate ? new Date(apiData.maturityDate) : undefined,
    faceValue: apiData.faceValue || 0,
    yield: apiData.yield || 0,
    dealerQuotedYieldPercent: apiData.dealerQuotedYieldPercent ?? undefined,
    treasuryBillListingId: apiData.treasuryBillListingId || '',
    pricePaid: apiData.pricePaid || 0, // Added for earnings calculation
    saleProceeds: apiData.saleProceeds ?? 0,
    orderDateTimeUtc: apiData.orderDateTimeUTC ? new Date(apiData.orderDateTimeUTC) : undefined,
    status: treasuryBillLifecycleFromStatus(apiData.status, apiData.faceValue ?? 0),
  }),

  paginatedFromApi: (
    response: PaginatedListOfTreasuryBillSummaryDto,
  ): TreasuryBillAssetSummary[] => {
    if (!response || !response.items) {
      return [];
    }
    return response.items.map(treasuryBillMapper.summaryFromApi);
  },

  lifecycleFromStatus: treasuryBillLifecycleFromStatus,

  requestToCommand: (request: RequestTreasuryBill): RequestTreasuryBillCommand => ({
    treasuryBillListingId: request.treasuryBillListingId,
    corporateEntityId: request.corporateEntityId,
    requestedPurchasePrice: request.requestedPurchasePrice,
    faceValue: request.faceValue,
  }),

  sellRequestToCommand: (request: SellTreasuryBill): CreateTreasuryBillSellOrderCommand => ({
    assetId: request.assetId,
    amount: request.amount,
  }),

  sellResponseFromApi: (orderId: string): string => orderId,

  fromApi: (
    request: PaginatedListOfTreasuryBillListingDto,
  ): PaginatedResponse<TreasuryBillListing> => {
    return {
      items: (request.items || []).map((item) => ({
        id: item.id || '',
        title: item.title || '',
        isin: item.isin || '',
        issuer: {
          code: item.issuer?.toString() || '',
        },
        issueDate: item.issueDate ? new Date(item.issueDate) : new Date(),
        maturityDate: item.maturityDate ? new Date(item.maturityDate) : new Date(),
        auctionDate: item.auctionDate ? new Date(item.auctionDate) : new Date(),
        // yieldAtAuction: item.yieldAtAuction || 0,
        marketYieldToMaturity: item.marketYieldToMaturity || 0,
        pricePer100FaceValue: item.pricePer100FaceValue || 0,
        midPricePer100FaceValue: item.midPricePer100FaceValue || 0,
        timeToMaturityInDays: item.timeToMaturityInDays || 0,
        isMatured: item.isMatured || false,
        maxAvailableFaceValue: item.maxAvailableFaceValue || 0,
      })),
      pageNumber: request.pageNumber || 1,
      totalPages: request.totalPages || 1,
      totalCount: request.totalCount || 0,
      hasPreviousPage: request.hasPreviousPage || false,
      hasNextPage: request.hasNextPage || false,
    };
  },

  detailFromApi: (apiData: TreasuryBillDto): TreasuryBillDetail => {
    const status = treasuryBillLifecycleFromStatus(apiData.status, apiData.faceValue ?? 0);
    if (!status) {
      throw new Error('Treasury bill detail has an invalid status');
    }

    return {
      id: apiData.id || '',
      pricePaid: apiData.pricePaid || 0,
      title: apiData.title || '',
      isin: apiData.isin || '',
      status,
      orderDateTimeUtc: apiData.orderDateTimeUTC ? new Date(apiData.orderDateTimeUTC) : new Date(),
      treasuryBillListingId: apiData.treasuryBillListingId || 0,
      faceValue: apiData.faceValue || 0,
      originalFaceValue: apiData.originalFaceValue || apiData.faceValue || 0,
      requestedPurchasePrice: apiData.requestedPurchasePrice || 0,
      snapshotYieldToMaturity: apiData.snapshotYieldToMaturity || 0,
      snapshotPricePer100FaceValue: apiData.snapshotPricePer100FaceValue || 0,
      // The API resolves this server-side; the coalesce is repeated here only so a DTO predating the
      // field still renders a real yield rather than a blank.
      yieldToMaturity:
        apiData.yieldToMaturity ??
        apiData.dealerQuotedYieldPercent ??
        apiData.snapshotYieldToMaturity ??
        0,
      dealerQuotedYieldPercent: apiData.dealerQuotedYieldPercent ?? undefined,
      issuer: treasuryBillMapper.issuerFromApi(apiData.issuer),
      maturityDate: apiData.maturityDate ? new Date(apiData.maturityDate) : new Date(),
      isOrder: apiData.isOrder || false,
      isAsset: apiData.isAsset || false,
      isMatured: apiData.isMatured || false,
      isSettled: apiData.isSettled || false,
      daysToMaturity: apiData.daysToMaturity || 0,
      currentEstimatedValue: apiData.currentEstimatedValue || 0,
      pendingSaleAmount: apiData.pendingSaleAmount ?? 0,
      pendingSaleCount: apiData.pendingSaleCount ?? 0,
      completedPartialSaleCount: apiData.completedPartialSaleCount ?? 0,
      saleProceeds: apiData.saleProceeds ?? 0,
      treasuryBillListing: treasuryBillMapper.listingNestedFromApi(apiData.treasuryBillListing),
      transactions: (apiData.transactions || []).map(treasuryBillMapper.transactionFromApi),
    };
  },

  listingNestedFromApi: (apiData: TreasuryBillListingNestedDto): TreasuryBillListingNested => ({
    publicId: apiData.publicId || '',
    title: apiData.title || '',
    isin: apiData.isin || '',
    issuer: treasuryBillMapper.issuerFromApi(apiData.issuer),
    maturityDate: apiData.maturityDate ? new Date(apiData.maturityDate) : new Date(),
    pricePer100FaceValue: apiData.pricePer100FaceValue || 0,
  }),

  transactionFromApi: (apiData: TreasuryBillTransactionDto): TreasuryBillTransaction => ({
    id: apiData.id || '',
    treasuryBillId: apiData.treasuryBillId || '',
    transactionType: treasuryBillMapper.transactionTypeFromApi(apiData.transactionType),
    transactionDateTime: apiData.transactionDateTime
      ? new Date(apiData.transactionDateTime)
      : new Date(),
    amount: apiData.amount || 0,
    notes: apiData.notes || '',
    transactionTypeDisplay: apiData.transactionTypeDisplay || '',
    transactionDescriptionDisplay: apiData.transactionDescriptionDisplay || '',
  }),

  issuerFromApi: (apiData?: IssuerDto): IssuerInfo => ({
    code: apiData?.toString() || '',
  }),

  transactionTypeFromApi: (type?: TransactionType): TreasuryBillTransactionType => {
    switch (type) {
      case TransactionType.Value0:
        return 'request';
      case TransactionType.Value1:
        return 'approval';
      case TransactionType.Value2:
        return 'rejection';
      case TransactionType.Value3:
        return 'issue';
      case TransactionType.Value4:
        return 'maturity';
      case TransactionType.Value5:
        return 'sale-request';
      case TransactionType.Value6:
        return 'sale';
      case TransactionType.Value7:
        return 'stake-sale-request';
      case TransactionType.Value8:
        return 'stake-sale';
      default:
        return 'other';
    }
  },
};
